<template>
  <div>
    <a-spin :spinning="loading">
      <div class="card">
        <div class="card-header">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              <strong>Admin Users</strong>
            </h5>
            <a-button type="primary" @click="showCreateModal" icon="plus">
              Add Admin
            </a-button>
          </div>
        </div>
        <div class="card-body">
          <a-table
            :columns="columns"
            :data-source="admins"
            :pagination="{ pageSize: 10 }"
            :row-key="(record) => record.id"
          >
            <template slot="name" slot-scope="text, record">
              {{ record.name }}
              <a-tag v-if="isCurrentUser(record)" class="ml-2">You</a-tag>
            </template>

            <template slot="status" slot-scope="text, record">
              <a-tag :color="record.isActive ? 'green' : 'red'">
                {{ record.isActive ? 'Active' : 'Disabled' }}
              </a-tag>
            </template>

            <template slot="lastLogin" slot-scope="text, record">
              <span v-if="record.lastLogin">{{ record.lastLogin }}</span>
              <span v-else class="text-muted">Never</span>
            </template>

            <template slot="actions" slot-scope="text, record">
              <span v-if="isCurrentUser(record)" class="text-muted">—</span>
              <a-button
                v-else-if="record.isActive"
                size="small"
                type="danger"
                @click="confirmDeactivate(record)"
              >
                <a-icon type="stop" /> Disable
              </a-button>
              <a-button v-else size="small" @click="reactivate(record)">
                <a-icon type="play-circle" /> Enable
              </a-button>
            </template>
          </a-table>
        </div>
      </div>
    </a-spin>

    <a-modal
      title="Add Admin User"
      :visible="modalVisible"
      :confirm-loading="modalLoading"
      ok-text="Create"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
      :width="600"
    >
      <a-alert
        message="The new admin will receive an email with a link to set their password."
        type="info"
        show-icon
        class="mb-3"
      />
      <a-form-model
        ref="adminForm"
        :model="form"
        :rules="rules"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
      >
        <a-form-model-item label="First name" prop="firstName">
          <a-input v-model="form.firstName" />
        </a-form-model-item>
        <a-form-model-item label="Last name" prop="lastName">
          <a-input v-model="form.lastName" />
        </a-form-model-item>
        <a-form-model-item label="Email" prop="username">
          <a-input v-model="form.username" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  Tag,
  Icon,
  Alert,
  Spin,
} from 'ant-design-vue'

Vue.use(Button, Table, Modal, Form, Input, Tag, Icon, Alert, Spin)

import { AdminUserService } from '@/services/api'

const emptyForm = () => ({ firstName: '', lastName: '', username: '' })

export default {
  data() {
    return {
      loading: false,
      modalVisible: false,
      modalLoading: false,
      admins: [],
      form: emptyForm(),

      columns: [
        {
          title: 'Name',
          key: 'name',
          scopedSlots: { customRender: 'name' },
          sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
          title: 'Email',
          dataIndex: 'username',
          key: 'username',
        },
        {
          title: 'Status',
          key: 'status',
          width: 120,
          scopedSlots: { customRender: 'status' },
          filters: [
            { text: 'Active', value: 'active' },
            { text: 'Disabled', value: 'disabled' },
          ],
          onFilter: (value, record) =>
            value === 'active' ? record.isActive : !record.isActive,
        },
        {
          title: 'Last login',
          key: 'lastLogin',
          width: 180,
          scopedSlots: { customRender: 'lastLogin' },
        },
        {
          title: 'Actions',
          key: 'actions',
          width: 140,
          scopedSlots: { customRender: 'actions' },
        },
      ],

      rules: {
        firstName: [
          { required: true, message: 'Please enter a first name', trigger: 'blur' },
        ],
        lastName: [
          { required: true, message: 'Please enter a last name', trigger: 'blur' },
        ],
        username: [
          { required: true, message: 'Please enter an email', trigger: 'blur' },
          { type: 'email', message: 'Please enter a valid email address', trigger: 'blur' },
        ],
      },
    }
  },

  methods: {
    async loadData() {
      this.loading = true
      try {
        const response = await AdminUserService.getAll()
        this.admins = response.data.data || []
      } catch (error) {
        this.$notification.error({
          message: 'Error Loading Data',
          description: 'Failed to load admin users.',
        })
      } finally {
        this.loading = false
      }
    },

    isCurrentUser(record) {
      return record.id === this.$store.state.user.id
    },

    showCreateModal() {
      this.form = emptyForm()
      if (this.$refs.adminForm) {
        this.$refs.adminForm.resetFields()
      }
      this.modalVisible = true
    },

    handleModalCancel() {
      this.modalVisible = false
    },

    async handleModalOk() {
      try {
        await this.$refs.adminForm.validate()
      } catch (e) {
        return
      }

      this.modalLoading = true
      try {
        await AdminUserService.create(this.form)
        this.$notification.success({
          message: 'Admin Created',
          description: `An activation email has been sent to ${this.form.username}.`,
        })
        this.modalVisible = false
        this.loadData()
      } catch (error) {
        // validation and server errors are shown by the axios interceptor
      } finally {
        this.modalLoading = false
      }
    },

    confirmDeactivate(record) {
      Modal.confirm({
        title: 'Disable Admin',
        content: `${record.name} will no longer be able to log in. You can enable the account again later.`,
        okText: 'Disable',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => this.deactivate(record),
      })
    },

    async deactivate(record) {
      try {
        await AdminUserService.deactivate(record.id)
        this.$notification.success({
          message: 'Admin Disabled',
          description: `${record.name} has been disabled.`,
        })
        record.isActive = false
      } catch (error) {
        // error message is shown by the axios interceptor
      }
    },

    async reactivate(record) {
      try {
        await AdminUserService.reactivate(record.id)
        this.$notification.success({
          message: 'Admin Enabled',
          description: `${record.name} has been enabled.`,
        })
        record.isActive = true
      } catch (error) {
        // error message is shown by the axios interceptor
      }
    },
  },

  mounted() {
    this.loadData()
  },
}
</script>

<style scoped>
.text-muted {
  color: #999;
}

.ml-2 {
  margin-left: 8px;
}

.mb-3 {
  margin-bottom: 16px;
}
</style>
